import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import {
  ageInYearsAtEventStart,
  formatDate,
  formatDateTime,
  participantHasMedicalInformation,
} from "@/lib/utils";
import { FormField } from "@/components/forms";
import { ParticipantGroupField } from "@/components/forms/ParticipantGroupField";
import { formStyles } from "@/components/forms/formStyles";
import { getToastStateFromLocationState } from "@/lib/navigation";
import { useToast } from "@/lib/ui";
import { handleApiError } from "@/lib/error-handling";
import {
  type AdminParticipantDetail,
  useAdminEvent,
  useAdminParticipant,
  useAdminParticipantApprove,
  useAdminParticipantCancel,
  useAdminParticipantReject,
  useAdminParticipantTakeBalance,
  useAdminParticipantUpdate,
  useAdminCheckoutPaymentLink,
} from "@/lib/admin-api";
import { useParticipantActions } from "./useParticipantActions";
import { ParticipantTimeline } from "./ParticipantTimeline";
import { RadioButton } from "primereact/radiobutton";
import { InputText } from "primereact/inputtext";
import {
  getApplicablePaymentLinkKinds,
  type PaymentLinkKind,
} from "@/lib/payment-status";

// Type for participant with refund fields and price point that may not be in generated types
type ParticipantWithRefunds = AdminParticipantDetail & {
  depositRefundId?: string | null;
  sharedPaymentIntentId?: string | null;
  sharedPaymentIntentStatus?: string | null;
  sharedAllocatedAmountCents?: number | null;
  sharedAllocatedRefundId?: string | null;
  sharedDepositPaymentIntentId?: string | null;
  sharedDepositPaymentIntentStatus?: string | null;
  sharedDepositAllocatedAmountCents?: number | null;
  sharedDepositAllocatedRefundId?: string | null;
  pricePoint?: { id: string; label: string } | null;
  boatId?: string | null;
  boat?: {
    id: string;
    class: string;
    sailNumber: string;
    status: string;
    paymentAmountCents: number | null;
    depositAmountCents: number | null;
    balanceDueAt: string | null;
    pricePoint?: { id: string; label: string } | null;
    sharedPaymentIntentStatus?: string | null;
    sharedAllocatedAmountCents?: number | null;
    sharedDepositPaymentIntentStatus?: string | null;
    sharedDepositAllocatedAmountCents?: number | null;
    stripePaymentIntentStatus?: string | null;
    depositStripePaymentIntentStatus?: string | null;
  } | null;
};

export function ParticipantDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { toastRef, showToast } = useToast();

  const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const [takeBalanceDialogVisible, setTakeBalanceDialogVisible] = useState(false);
  const [paymentLinkDialogVisible, setPaymentLinkDialogVisible] = useState(false);
  const [paymentLinkKind, setPaymentLinkKind] = useState<PaymentLinkKind>("full");
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRefund, setCancelRefund] = useState(true);

  const participantQuery = useAdminParticipant(id);
  const participant = (participantQuery.data?.participant ?? null) as ParticipantWithRefunds | null;

  const eventId = participant?.eventId;
  const eventQuery = useAdminEvent(eventId);
  const requiresConsentForm = Boolean(
    eventQuery.data?.event?.requiresConsentForm,
  );

  // Note: checkout is included in the API response via participantReadInclude,
  // but not in the generated types. Using type assertion to access it.
  const checkoutData = (participant as {
    checkout?: { id?: string; bookingReference?: string | null };
  } | null)?.checkout;
  const bookingReference = checkoutData?.bookingReference ?? null;
  const checkoutId = checkoutData?.id ?? null;

  const approveMutation = useAdminParticipantApprove();
  const rejectMutation = useAdminParticipantReject();
  const cancelMutation = useAdminParticipantCancel();
  const takeBalanceMutation = useAdminParticipantTakeBalance();
  const paymentLinkMutation = useAdminCheckoutPaymentLink();
  const updateMutation = useAdminParticipantUpdate();

  const actions = useParticipantActions({
    participantId: id,
    participant,
    approve: approveMutation,
    reject: rejectMutation,
    cancel: cancelMutation,
    showToast,
  });

  useEffect(() => {
    if (!id) {
      void navigate("/checkouts");
    }
  }, [id, navigate]);

  // Show toast from router state (set by PendingCheckouts on redirect)
  useEffect(() => {
    if (participantQuery.isLoading) return;

    const toastState = getToastStateFromLocationState(location.state);
    if (!toastState) return;

    showToast(toastState);
    void navigate(location.pathname, { replace: true, state: {} });
  }, [
    location.pathname,
    location.state,
    navigate,
    participantQuery.isLoading,
    showToast,
  ]);

  const handleRejectClick = () => {
    setRejectReason("");
    setRejectDialogVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) return;
    setRejectDialogVisible(false);
    const ok = await actions.handleReject(rejectReason);
    if (ok) {
      setRejectReason("");
    }
  };

  const handleCancelClick = () => {
    setCancelReason("");
    const hasBalancePayment =
      participant?.status === "APPROVED" &&
      (Boolean(participant?.stripePaymentIntentId) ||
        Boolean(participant?.sharedPaymentIntentId));
    const hasDepositPayment =
      participant?.status === "APPROVED" &&
      ((Boolean(participant?.depositStripePaymentIntentId) &&
        participant?.depositStripePaymentIntentStatus === "succeeded") ||
        (Boolean(participant?.sharedDepositPaymentIntentId) &&
          participant?.sharedDepositPaymentIntentStatus === "succeeded"));
    const hasPaymentCaptured = hasBalancePayment || hasDepositPayment;
    setCancelRefund(hasPaymentCaptured ? true : false);
    setCancelDialogVisible(true);
  };

  const handleCancelConfirm = async () => {
    if (!cancelReason.trim()) return;
    setCancelDialogVisible(false);
    const ok = await actions.handleCancel({
      reason: cancelReason,
      refund: cancelRefund,
    });
    if (ok) {
      setCancelReason("");
      setCancelRefund(false);
    }
  };

  const handleTakeBalanceClick = () => {
    setTakeBalanceDialogVisible(true);
  };

  const handleOpenPaymentLinkDialog = () => {
    if (!participant) return;
    const kinds = getApplicablePaymentLinkKinds(participant);
    if (kinds.length === 0) {
      showToast({
        severity: "warn",
        summary: "No payment due",
        detail:
          "This participant has no applicable deposit, balance, or full payment to collect.",
      });
      return;
    }
    setPaymentLinkKind(kinds[0]);
    setPaymentLinkUrl(null);
    setPaymentLinkDialogVisible(true);
  };

  const handleGeneratePaymentLink = async () => {
    if (!checkoutId || !id) return;
    try {
      const result = await paymentLinkMutation.mutateAsync({
        params: { path: { id: checkoutId } },
        body: {
          paymentKind: paymentLinkKind,
          targetType: "participants",
          targetIds: [id],
        },
      });
      setPaymentLinkUrl(result.sessionUrl);
      await window.navigator.clipboard.writeText(result.sessionUrl);
      showToast({
        severity: "success",
        summary: "Payment link created",
        detail: "Link copied to clipboard",
      });
    } catch (error: unknown) {
      const { message } = handleApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to create payment link",
      });
    }
  };

  const handleCopyPaymentLink = async () => {
    if (!paymentLinkUrl) return;
    await window.navigator.clipboard.writeText(paymentLinkUrl);
    showToast({
      severity: "success",
      summary: "Copied",
      detail: "Payment link copied to clipboard",
    });
  };

  const handleTakeBalanceConfirm = async () => {
    if (!id) return;
    setTakeBalanceDialogVisible(false);
    try {
      await takeBalanceMutation.mutateAsync({
        params: { path: { id } },
        body: {} as Record<string, never>,
      });
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Balance payment processed",
      });
      void participantQuery.refetch();
    } catch (error: unknown) {
      const { message } = handleApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to process balance payment",
      });
    }
  };

  const handleGroupSave = async (group: string | null) => {
    if (!id || !participant) return;
    if ((participant.group ?? null) === group) return;

    try {
      await updateMutation.mutateAsync({
        params: { path: { id } },
        body: { group },
      });
    } catch (error: unknown) {
      const { message } = handleApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to update participant group",
      });
    }
  };

  const formatPrice = (cents: number | null | undefined) => {
    if (cents == null || cents === 0) return "Free";
    return `£${(cents / 100).toFixed(2)}`;
  };

  // Calculate payment status
  const paymentStatus = (() => {
    if (!participant) return null;

    const boat = participant.boat;
    const hasDeposit =
      (participant.depositAmountCents != null &&
        participant.depositAmountCents > 0) ||
      (boat?.depositAmountCents != null && boat.depositAmountCents > 0);
    const depositPaid =
      participant.depositStripePaymentIntentStatus === "succeeded" ||
      participant.sharedDepositPaymentIntentStatus === "succeeded" ||
      boat?.depositStripePaymentIntentStatus === "succeeded" ||
      boat?.sharedDepositPaymentIntentStatus === "succeeded";
    const balancePaid =
      participant.stripePaymentIntentStatus === "succeeded" ||
      boat?.stripePaymentIntentStatus === "succeeded";
    const sharedFullOrBalancePaid =
      participant.sharedPaymentIntentStatus === "succeeded" ||
      boat?.sharedPaymentIntentStatus === "succeeded";

    if (!hasDeposit) {
      return balancePaid || sharedFullOrBalancePaid
        ? "FULLY_PAID"
        : "NOTHING_PAID";
    }

    if (balancePaid || sharedFullOrBalancePaid) {
      return "FULLY_PAID";
    }
    if (depositPaid) {
      return "DEPOSIT_ONLY_PAID";
    }
    return "NOTHING_PAID";
  })();

  // Check if balance can be taken
  const canTakeBalance = (() => {
    if (!participant) return false;
    if (participant.status !== "APPROVED") return false;
    if (paymentStatus === "FULLY_PAID") return false;
    if (paymentStatus !== "DEPOSIT_ONLY_PAID") return false;
    // Balance hasn't been attempted yet or failed
    if (participant.sharedPaymentIntentId) return false;
    return (
      !participant.stripePaymentIntentId ||
      participant.stripePaymentIntentStatus === "failed" ||
      participant.sharedPaymentIntentStatus === "failed"
    );
  })();

  const applicablePaymentLinkKinds = participant
    ? getApplicablePaymentLinkKinds(participant)
    : [];
  const paymentLinkKindLabels: Record<PaymentLinkKind, string> = {
    deposit: "Deposit",
    balance: "Balance",
    full: "Full amount",
  };

  // Calculate balance amount
  const balanceAmountCents = (() => {
    if (!participant) return null;
    const boat = participant.boat;
    const deposit =
      participant.depositAmountCents ??
      (boat?.depositAmountCents != null && boat.depositAmountCents > 0
        ? boat.depositAmountCents
        : null);
    if (!deposit) return null;
    const total =
      (participant.paymentAmountCents ?? 0) > 0
        ? (participant.paymentAmountCents ?? 0)
        : (boat?.paymentAmountCents ?? 0);
    return total - deposit;
  })();

  if (participantQuery.isLoading) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <p>Loading participant details...</p>
        </div>
      </div>
    );
  }

  if (participantQuery.isError || !participant) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <p>Participant not found</p>
          <BackButton className="mt-2" />
        </div>
      </div>
    );
  }

  const eventTitle = (() => {
    const title = eventQuery.data?.event?.title;
    return typeof title === "string" && title.trim() !== "" ? title : null;
  })();

  const adminEvent = eventQuery.data?.event;
  const participantAgeAtEventStart =
    participant.consentFormParticipantDateOfBirth &&
    adminEvent?.startsAt &&
    adminEvent.timezone
      ? ageInYearsAtEventStart(
          participant.consentFormParticipantDateOfBirth,
          adminEvent.startsAt,
          adminEvent.timezone,
        )
      : null;

  const isBoatCrew = participant.boatId != null;
  const boat = participant.boat;
  const entryAmountCents =
    (participant.paymentAmountCents ?? 0) > 0
      ? participant.paymentAmountCents
      : boat?.paymentAmountCents;
  const depositAmountCents =
    participant.depositAmountCents ??
    (boat?.depositAmountCents != null && boat.depositAmountCents > 0
      ? boat.depositAmountCents
      : null);
  const pricePointLabel =
    participant.pricePoint?.label ?? boat?.pricePoint?.label ?? null;
  const balanceDueAtValue = participant.balanceDueAt ?? boat?.balanceDueAt ?? null;

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <div className="mb-4">
        <div className="flex align-items-center gap-3 mb-4 flex-wrap">
          <BackButton />
          <h1 className="text-2xl font-bold m-0">Participant Details</h1>
          <div className="flex gap-2 ml-auto flex-wrap align-items-center">
            {isBoatCrew ? (
              <span className="text-500 text-sm italic mr-2">
                Manage boat crew via the boat on the checkout page
              </span>
            ) : (
              <>
                {actions.canApprove && (
                  <Button
                    label="Approve"
                    icon="pi pi-check"
                    className="p-button-success"
                    onClick={() => void actions.handleApprove()}
                    loading={actions.actionLoading === "approve"}
                    disabled={actions.actionLoading !== null}
                  />
                )}
                {canTakeBalance && (
                  <Button
                    label="Take Balance"
                    icon="pi pi-credit-card"
                    className="p-button-info"
                    onClick={handleTakeBalanceClick}
                    loading={takeBalanceMutation.isPending}
                    disabled={
                      actions.actionLoading !== null ||
                      takeBalanceMutation.isPending
                    }
                  />
                )}
                {checkoutId && (
                  <Button
                    label="Payment Link"
                    icon="pi pi-link"
                    className="p-button-outlined"
                    onClick={handleOpenPaymentLinkDialog}
                    loading={paymentLinkMutation.isPending}
                    disabled={
                      actions.actionLoading !== null ||
                      paymentLinkMutation.isPending
                    }
                  />
                )}
                {actions.canReject && (
                  <Button
                    label="Reject"
                    icon="pi pi-times"
                    className="p-button-danger"
                    onClick={handleRejectClick}
                    loading={actions.actionLoading === "reject"}
                    disabled={actions.actionLoading !== null}
                  />
                )}
                {actions.canCancel && (
                  <Button
                    label="Cancel"
                    icon="pi pi-ban"
                    className="p-button-warning"
                    onClick={handleCancelClick}
                    loading={actions.actionLoading === "cancel"}
                    disabled={actions.actionLoading !== null}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <Panel header="Basic Information" className="mb-3">
            <div className="flex flex-column gap-2">
              <div>
                <span className="font-semibold">Name: </span>
                {participant.firstName} {participant.lastName}
              </div>
              <div>
                <span className="font-semibold">Status: </span>
                <Tag
                  value={participant.status}
                  severity={
                    participant.status === "APPROVED"
                      ? "success"
                      : participant.status === "PENDING"
                        ? "warning"
                        : participant.status === "REJECTED"
                          ? "danger"
                          : "secondary"
                  }
                />
              </div>
              {participant.eventId ? (
                <>
                  <div>
                    <span className="font-semibold">Event: </span>
                    <Link
                      to={`/events/${participant.eventId}`}
                      className="text-primary hover:underline"
                    >
                      {eventTitle ?? participant.eventId}
                    </Link>
                  </div>
                  {adminEvent?.startsAt ? (
                    <div>
                      <span className="font-semibold">Event start: </span>
                      {formatDate(adminEvent.startsAt, adminEvent.timezone)}
                    </div>
                  ) : null}
                  {adminEvent?.groupsEnabled ? (
                    <div className="flex align-items-center gap-2 flex-wrap">
                      <span className="font-semibold">Participant group: </span>
                      <ParticipantGroupField
                        id="participant-group"
                        eventId={participant.eventId}
                        value={participant.group}
                        onSave={handleGroupSave}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {(participant as unknown as { boat?: { class: string; sailNumber: string } })
                .boat ? (
                <div>
                  <span className="font-semibold">Boat: </span>
                  {(participant as unknown as { boat: { class: string; sailNumber: string } })
                    .boat.class}{" "}
                  {(participant as unknown as { boat: { class: string; sailNumber: string } })
                    .boat.sailNumber}
                </div>
              ) : null}

              {/* Payment Information Section */}
              <div className="mt-2 pt-2 border-top-1 border-300">
                <div className="font-semibold mb-2">Payment Information:</div>
                <div className="flex flex-column gap-2">
                  {depositAmountCents != null && depositAmountCents > 0 ? (
                    <>
                      <div className="flex align-items-center gap-2 flex-wrap">
                        <span className="font-semibold">
                          {isBoatCrew ? "Boat entry total:" : "Total Amount:"}{" "}
                        </span>
                        <span>{formatPrice(entryAmountCents)}</span>
                      </div>
                      {pricePointLabel ? (
                        <div className="flex align-items-center gap-2">
                          <span className="font-semibold">Price point: </span>
                          {pricePointLabel}
                        </div>
                      ) : null}
                      <div className="flex align-items-center gap-2 flex-wrap">
                        <span className="font-semibold">Deposit: </span>
                        <span>{formatPrice(depositAmountCents)}</span>
                        {(participant.depositStripePaymentIntentStatus === "refunded" ||
                          participant.depositRefundId) && (
                          <Tag value="Refunded" severity="info" />
                        )}
                      </div>
                      {balanceAmountCents != null && balanceAmountCents > 0 && (
                        <div className="flex align-items-center gap-2 flex-wrap">
                          <span className="font-semibold">Balance: </span>
                          <span>{formatPrice(balanceAmountCents)}</span>
                          {(participant.stripePaymentIntentStatus === "refunded" ||
                            participant.stripeRefundId) && (
                            <Tag value="Refunded" severity="info" />
                          )}
                          {balanceDueAtValue ? (
                            <span className="text-sm text-gray-600 ml-2">
                              (due {formatDateTime(balanceDueAtValue)})
                            </span>
                          ) : null}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex align-items-center gap-2 flex-wrap">
                        <span className="font-semibold">
                          {isBoatCrew ? "Boat entry:" : "Price:"}{" "}
                        </span>
                        <span>{formatPrice(entryAmountCents)}</span>
                        {(participant.stripePaymentIntentStatus === "refunded" ||
                          participant.stripeRefundId) && (
                          <Tag value="Refunded" severity="info" />
                        )}
                      </div>
                      {isBoatCrew ? (
                        <div className="text-sm text-gray-600">
                          Charged once per boat, not per crew member.
                        </div>
                      ) : null}
                      {pricePointLabel ? (
                        <div className="flex align-items-center gap-2">
                          <span className="font-semibold">Price point: </span>
                          {pricePointLabel}
                        </div>
                      ) : null}
                    </>
                  )}

                  {(entryAmountCents ?? 0) > 0 && (
                    <div className="flex align-items-center gap-2 flex-wrap">
                      <span className="font-semibold">Payment Status: </span>
                      {paymentStatus === "FULLY_PAID" && (
                        <Tag value="Fully Paid" severity="success" />
                      )}
                      {paymentStatus === "DEPOSIT_ONLY_PAID" && (
                        <Tag value="Deposit Paid" severity="warning" />
                      )}
                      {paymentStatus === "NOTHING_PAID" && (
                        <Tag value="Not Paid" severity="danger" />
                      )}
                      {participant.balanceCronExcluded && (
                        <Tag value="Auto-collect off" severity="warning" />
                      )}
                    </div>
                  )}
                </div>

                {/* Refund Information */}
                {((participant.depositStripePaymentIntentStatus === "refunded" ||
                  participant.depositRefundId) ||
                  participant.stripePaymentIntentStatus === "refunded" ||
                  participant.stripeRefundId ||
                  participant.sharedPaymentIntentStatus === "refunded" ||
                  participant.sharedAllocatedRefundId ||
                  participant.sharedDepositPaymentIntentStatus === "refunded" ||
                  participant.sharedDepositAllocatedRefundId) && (
                  <div className="mt-2 pt-2 border-top-1 border-300">
                    <div className="font-semibold mb-1">Refund Information:</div>
                    {participant.depositStripePaymentIntentStatus === "refunded" ||
                    participant.depositRefundId ||
                    participant.sharedDepositPaymentIntentStatus === "refunded" ||
                    participant.sharedDepositAllocatedRefundId ? (
                      <div className="text-sm">
                        <span className="font-semibold">Deposit Refund: </span>
                        <span className="text-green-600">Refunded</span>
                        {participant.sharedDepositAllocatedAmountCents != null && (
                          <span className="text-gray-600 ml-2">
                            (Amount:{" "}
                            {formatPrice(
                              participant.sharedDepositAllocatedAmountCents,
                            )}
                            )
                          </span>
                        )}
                      </div>
                    ) : null}
                    {participant.stripePaymentIntentStatus === "refunded" ||
                    participant.stripeRefundId ||
                    participant.sharedPaymentIntentStatus === "refunded" ||
                    participant.sharedAllocatedRefundId ? (
                      <div className="text-sm">
                        <span className="font-semibold">Balance Refund: </span>
                        <span className="text-green-600">Refunded</span>
                        {participant.sharedAllocatedAmountCents != null && (
                          <span className="text-gray-600 ml-2">
                            (Amount:{" "}
                            {formatPrice(participant.sharedAllocatedAmountCents)})
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              {bookingReference ? (
                <div>
                  <span className="font-semibold">Booking Reference: </span>
                  {checkoutId ? (
                    <a
                      href={`/checkouts/${checkoutId}`}
                      onClick={(e) => {
                        e.preventDefault();
                        void navigate(`/checkouts/${checkoutId}`);
                      }}
                      className="text-primary hover:underline"
                    >
                      {bookingReference}
                    </a>
                  ) : (
                    bookingReference
                  )}
                </div>
              ) : null}
              {participant.userId ? (
                <div>
                  <span className="font-semibold">User: </span>
                  <a
                    href={`/users/${participant.userId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      void navigate(`/users/${participant.userId}`);
                    }}
                    className="text-primary hover:underline"
                  >
                    {participant.firstName} {participant.lastName}
                  </a>
                </div>
              ) : null}
              {participant.bookingEmailAddress && (
                <div>
                  <span className="font-semibold">Booking Email: </span>
                  {participant.bookingEmailAddress}
                </div>
              )}
              <div>
                <span className="font-semibold">Created: </span>
                {participant.createdAt ? formatDateTime(participant.createdAt) : "-"}
              </div>
              {participant.approvedAt && (
                <div>
                  <span className="font-semibold">Approved: </span>
                  {formatDateTime(participant.approvedAt)}
                </div>
              )}
              {participant.rejectedAt && (
                <>
                  <div>
                    <span className="font-semibold">Rejected: </span>
                    {formatDateTime(participant.rejectedAt)}
                  </div>
                  {participant.rejectionReason && (
                    <div>
                      <span className="font-semibold">Reason: </span>
                      {participant.rejectionReason}
                    </div>
                  )}
                </>
              )}
              {participant.cancelledAt && (
                <>
                  <div>
                    <span className="font-semibold">Cancelled: </span>
                    {formatDateTime(participant.cancelledAt)}
                  </div>
                  {participant.cancellationReason && (
                    <div>
                      <span className="font-semibold">Reason: </span>
                      {participant.cancellationReason}
                    </div>
                  )}
                </>
              )}
            </div>
          </Panel>
        </div>

        <div>
          <Panel header="Consents" className="mb-3">
            <div className="flex flex-column gap-2">
              <div>
                <span className="font-semibold">Code of Conduct: </span>
                {participant.consentFormCodeOfConductAccepted ? (
                  <Tag value="Accepted" severity="success" />
                ) : (
                  <Tag value="Not Accepted" severity="danger" />
                )}
              </div>
              <div>
                <span className="font-semibold">Data Processing: </span>
                {participant.dataProcessingConsent ? (
                  <Tag value="Consented" severity="success" />
                ) : (
                  <Tag value="Not Consented" severity="danger" />
                )}
              </div>
              {requiresConsentForm && (
                <div>
                  <span className="font-semibold">Emergency Treatment: </span>
                  {participant.emergencyTreatmentConsent === true ? (
                    <Tag value="Consented" severity="success" />
                  ) : participant.emergencyTreatmentConsent === false ? (
                    <Tag value="Not Consented" severity="danger" />
                  ) : (
                    <Tag value="N/A" severity="info" />
                  )}
                </div>
              )}
              <div>
                <span className="font-semibold">Photo Consent: </span>
                {participant.photoConsent === true ? (
                  <Tag value="Yes" severity="success" />
                ) : participant.photoConsent === false ? (
                  <Tag value="No" severity="warning" />
                ) : (
                  <Tag value="Not Provided" severity="info" />
                )}
              </div>
              <div>
                <span className="font-semibold">Marketing Consent: </span>
                {participant.marketingConsent ? (
                  <Tag value="Consented" severity="success" />
                ) : (
                  <Tag value="Not Consented" severity="info" />
                )}
              </div>
            </div>
          </Panel>
        </div>

        {requiresConsentForm && (
          <>
            <div>
              <Panel header="Participant Details" className="mb-3">
                <div className="flex flex-column gap-2">
                  {participant.consentFormParticipantDateOfBirth && (
                    <div>
                      <span className="font-semibold">Date of Birth: </span>
                      {participant.consentFormParticipantDateOfBirth}
                      {participantAgeAtEventStart != null ? (
                        <span className="text-gray-600 ml-2">
                          ({participantAgeAtEventStart} yoa)
                        </span>
                      ) : null}
                    </div>
                  )}
                  {participant.consentFormParticipantPronouns && (
                    <div>
                      <span className="font-semibold">Pronouns: </span>
                      {participant.consentFormParticipantPronouns}
                    </div>
                  )}
                  {participant.consentFormParticipantAddress && (
                    <div>
                      <span className="font-semibold">Address: </span>
                      <pre className="text-sm mt-1 whitespace-pre-wrap">
                        {participant.consentFormParticipantAddress}
                      </pre>
                    </div>
                  )}
                </div>
              </Panel>
            </div>

            {participant.consentFormParentCarerFirstName && (
              <div>
                <Panel header="Parent/Carer Information" className="mb-3">
                  <div className="flex flex-column gap-2">
                    <div>
                      <span className="font-semibold">Name: </span>
                      {participant.consentFormParentCarerFirstName}{" "}
                      {participant.consentFormParentCarerSurname}
                    </div>
                    {participant.consentFormParentCarerRelationship && (
                      <div>
                        <span className="font-semibold">Relationship: </span>
                        {participant.consentFormParentCarerRelationship}
                      </div>
                    )}
                    {participant.consentFormParentCarerAddress && (
                      <div>
                        <span className="font-semibold">Address: </span>
                        <pre className="text-sm mt-1 whitespace-pre-wrap">
                          {participant.consentFormParentCarerAddress}
                        </pre>
                      </div>
                    )}
                    {participant.consentFormParentCarerContactNumber && (
                      <div>
                        <span className="font-semibold">Contact: </span>
                        {participant.consentFormParentCarerContactNumber}
                      </div>
                    )}
                  </div>
                </Panel>
              </div>
            )}

            <div>
              <Panel header="Emergency Contacts" className="mb-3">
                <div className="flex flex-column gap-3">
                  {participant.consentFormEmergencyContact1Name && (
                    <div>
                      <span className="font-semibold">
                        Emergency Contact 1:{" "}
                      </span>
                      <div className="ml-4 mt-1">
                        <div>{participant.consentFormEmergencyContact1Name}</div>
                        {participant.consentFormEmergencyContact1Relationship && (
                          <div className="text-sm text-gray-600">
                            {participant.consentFormEmergencyContact1Relationship}
                          </div>
                        )}
                        {participant.consentFormEmergencyContact1Phone && (
                          <div className="text-sm text-gray-600">
                            {participant.consentFormEmergencyContact1Phone}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {participant.consentFormEmergencyContact2Name && (
                    <div>
                      <span className="font-semibold">
                        Emergency Contact 2:{" "}
                      </span>
                      <div className="ml-4 mt-1">
                        <div>{participant.consentFormEmergencyContact2Name}</div>
                        {participant.consentFormEmergencyContact2Relationship && (
                          <div className="text-sm text-gray-600">
                            {participant.consentFormEmergencyContact2Relationship}
                          </div>
                        )}
                        {participant.consentFormEmergencyContact2Phone && (
                          <div className="text-sm text-gray-600">
                            {participant.consentFormEmergencyContact2Phone}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Panel>
            </div>
          </>
        )}

        {requiresConsentForm &&
          participantHasMedicalInformation(participant) && (
            <div className="col-span-full">
              <Panel header="Medical Information" className="mb-3">
                <div className="flex flex-column gap-2">
                  {participant.consentFormImportantMedicalInformation && (
                    <div>
                      <span className="font-semibold">
                        Important Medical Information:{" "}
                      </span>
                      <pre className="text-sm mt-1 whitespace-pre-wrap">
                        {participant.consentFormImportantMedicalInformation}
                      </pre>
                    </div>
                  )}
                  {participant.consentFormMedicationDetails && (
                    <div>
                      <span className="font-semibold">Medication: </span>
                      <pre className="text-sm mt-1 whitespace-pre-wrap">
                        {participant.consentFormMedicationDetails}
                      </pre>
                    </div>
                  )}
                  {participant.consentFormInjuriesDetails && (
                    <div>
                      <span className="font-semibold">Injuries: </span>
                      <pre className="text-sm mt-1 whitespace-pre-wrap">
                        {participant.consentFormInjuriesDetails}
                      </pre>
                    </div>
                  )}
                  {participant.consentFormDietaryRequirementsDetails && (
                    <div>
                      <span className="font-semibold">
                        Dietary Requirements:{" "}
                      </span>
                      <pre className="text-sm mt-1 whitespace-pre-wrap">
                        {participant.consentFormDietaryRequirementsDetails}
                      </pre>
                    </div>
                  )}
                  {participant.consentFormDisabilityDetails && (
                    <div>
                      <span className="font-semibold">Disability: </span>
                      <pre className="text-sm mt-1 whitespace-pre-wrap">
                        {participant.consentFormDisabilityDetails}
                      </pre>
                    </div>
                  )}
                </div>
              </Panel>
            </div>
          )}
      </div>

      {/* Timeline */}
      <div className="mt-4">
        <ParticipantTimeline participant={participant} />
      </div>

      {/* Reject Dialog */}
      <Dialog
        header="Reject Participant"
        visible={rejectDialogVisible}
        style={{ width: "500px" }}
        onHide={() => setRejectDialogVisible(false)}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setRejectDialogVisible(false)}
              disabled={actions.actionLoading !== null}
            />
            <Button
              label="Reject"
              icon="pi pi-times"
              className="p-button-danger"
              onClick={() => void handleRejectConfirm()}
              loading={actions.actionLoading === "reject"}
              disabled={!rejectReason.trim() || actions.actionLoading !== null}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <FormField id="reject-reason" label="Reason for Rejection" required>
            <InputTextarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={5}
              placeholder="Please provide a reason for rejecting this participant..."
              className="w-full"
              style={formStyles.input}
              autoFocus
            />
          </FormField>
        </div>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        header="Cancel Participant"
        visible={cancelDialogVisible}
        style={{ width: "500px" }}
        onHide={() => setCancelDialogVisible(false)}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setCancelDialogVisible(false)}
              disabled={actions.actionLoading !== null}
            />
            <Button
              label="Cancel Participant"
              icon="pi pi-ban"
              className="p-button-warning"
              onClick={() => void handleCancelConfirm()}
              loading={actions.actionLoading === "cancel"}
              disabled={!cancelReason.trim() || actions.actionLoading !== null}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <FormField
            id="cancel-reason"
            label="Reason for Cancellation"
            required
          >
            <InputTextarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={5}
              placeholder="Please provide a reason for cancelling this participant..."
              className="w-full"
              style={formStyles.input}
              autoFocus
            />
          </FormField>

          {participant.status === "APPROVED" &&
          (Boolean(participant.stripePaymentIntentId) ||
            Boolean(participant.sharedPaymentIntentId) ||
            (Boolean(participant.depositStripePaymentIntentId) &&
              participant.depositStripePaymentIntentStatus === "succeeded") ||
            (Boolean(participant.sharedDepositPaymentIntentId) &&
              participant.sharedDepositPaymentIntentStatus ===
                "succeeded")) ? (
            <div
              className="flex align-items-center gap-2"
              style={{ marginTop: "1rem" }}
            >
              <Checkbox
                inputId="cancel-refund"
                checked={cancelRefund}
                onChange={(e) => setCancelRefund(e.checked ?? false)}
              />
              <label htmlFor="cancel-refund" className="cursor-pointer">
                Issue refund
                {(() => {
                  const hasDeposit =
                    (Boolean(participant.depositStripePaymentIntentId) &&
                      participant.depositStripePaymentIntentStatus ===
                        "succeeded") ||
                    (Boolean(participant.sharedDepositPaymentIntentId) &&
                      participant.sharedDepositPaymentIntentStatus ===
                        "succeeded");
                  const hasBalance =
                    Boolean(participant.stripePaymentIntentId) ||
                    Boolean(participant.sharedPaymentIntentId);
                  if (hasDeposit && hasBalance) return " (deposit and balance)";
                  if (hasDeposit) return " (deposit)";
                  return " (balance)";
                })()}
              </label>
            </div>
          ) : null}
        </div>
      </Dialog>

      {/* Take Balance Dialog */}
      <Dialog
        header="Take Balance Payment"
        visible={takeBalanceDialogVisible}
        style={{ width: "500px" }}
        onHide={() => setTakeBalanceDialogVisible(false)}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setTakeBalanceDialogVisible(false)}
              disabled={takeBalanceMutation.isPending}
            />
            <Button
              label="Take Balance"
              icon="pi pi-credit-card"
              className="p-button-info"
              onClick={() => void handleTakeBalanceConfirm()}
              loading={takeBalanceMutation.isPending}
              disabled={takeBalanceMutation.isPending}
            />
          </div>
        }
      >
        <div style={{ padding: "1rem" }}>
          <p>
            Are you sure you want to process the balance payment of{" "}
            <strong>{formatPrice(balanceAmountCents)}</strong> for this
            participant?
          </p>
          {participant && participant.balanceDueAt && (
            <p className="mt-2 text-sm text-gray-600">
              Balance due: {formatDateTime(participant.balanceDueAt)}
            </p>
          )}
        </div>
      </Dialog>

      <Dialog
        header="Generate Payment Link"
        visible={paymentLinkDialogVisible}
        style={{ width: "500px" }}
        onHide={() => {
          setPaymentLinkDialogVisible(false);
          setPaymentLinkUrl(null);
        }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Close"
              className="p-button-text"
              onClick={() => {
                setPaymentLinkDialogVisible(false);
                setPaymentLinkUrl(null);
              }}
              disabled={paymentLinkMutation.isPending}
            />
            {paymentLinkUrl ? (
              <Button
                label="Copy Link"
                icon="pi pi-copy"
                onClick={() => void handleCopyPaymentLink()}
              />
            ) : (
              <Button
                label="Generate"
                icon="pi pi-link"
                onClick={() => void handleGeneratePaymentLink()}
                loading={paymentLinkMutation.isPending}
                disabled={
                  paymentLinkMutation.isPending ||
                  !checkoutId ||
                  applicablePaymentLinkKinds.length === 0 ||
                  !applicablePaymentLinkKinds.includes(paymentLinkKind)
                }
              />
            )}
          </div>
        }
      >
        {applicablePaymentLinkKinds.length === 0 ? (
          <p className="m-0 text-color-secondary">
            No applicable payment types for this participant.
          </p>
        ) : (
          <div className="flex flex-column gap-2 mb-3">
            {applicablePaymentLinkKinds.map((value) => (
              <div key={value} className="flex align-items-center gap-2">
                <RadioButton
                  inputId={`participant-payment-link-kind-${value}`}
                  name="participantPaymentLinkKind"
                  value={value}
                  checked={paymentLinkKind === value}
                  onChange={() => {
                    setPaymentLinkKind(value);
                    setPaymentLinkUrl(null);
                  }}
                />
                <label htmlFor={`participant-payment-link-kind-${value}`}>
                  {paymentLinkKindLabels[value]}
                </label>
              </div>
            ))}
          </div>
        )}
        {paymentLinkUrl && (
          <InputText value={paymentLinkUrl} readOnly className="w-full" />
        )}
      </Dialog>
    </div>
  );
}


